import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import applicationService from '@/shared/services/applicationService';
import useRevealAlert from '@/shared/hooks/useRevealAlert';
import termService from '@/shared/services/termService';
import {
    OTHER_PART_OF_TERM,
    PART_OF_TERM_MAX_LENGTH,
    partOfTermOptionLabel,
    partsOfTermOf,
} from '@/shared/utils/partsOfTerm';
import {
    getCourseConfirmationOtherFieldErrors,
    MeetingFields,
    MODALITY_OPTIONS,
} from './CreateApplicationSection1';
import {
    LA_NOMINEE_DECLINE_OPTIONS,
    LA_SUPPORT_USE_CASE_OPTIONS,
} from './CreateApplicationSection5';
import { MEETING_HOUR_OPTIONS, meetingsFromSaved } from './meetings';
import { nomineesOf, requestedLACount } from './NomineeOfferPicker';
import { laStandingOf } from './NomineesPanel';

/**
 * A program coordinator's edit of a submitted request, on the faculty
 * member's behalf (PUT /api/application/:id/coordinator-edit). The editor
 * offers only what the server lets a coordinator change: the shared course
 * details, and the answers of the ranked programs the viewer coordinates
 * (every ranked program for an admin). The ranking, the CRN and the section
 * stay the faculty member's. Only the fields changed here are sent.
 *
 * Once LA has awarded support, the LA count edits the award, not the
 * request: the faculty member's request stays as they filed it.
 */

// The server refuses a draft (its applicant's alone), a withdrawn request,
// and one the faculty member is revising (their save would undo an edit)
const NOT_EDITABLE = ['draft', 'withdrawn', 'revision_requested'];

const LA_COUNTS = [1, 2, 3];

/**
 * The ranked programs whose answers this viewer edits, first choice first:
 * every one for an admin, otherwise the ones they coordinate. Empty when the
 * viewer can't edit the request at all.
 */
export function coordinatorEditablePrograms(application, roles = []) {
    if (!application || NOT_EDITABLE.includes(application.status)) return [];
    return coordinatedPrograms(application, roles);
}

// The ranked programs this viewer coordinates, whatever the status
function coordinatedPrograms(application, roles) {
    const ranked = [...(application.programPreferences || [])]
        .sort((a, b) => a.rank - b.rank)
        .map((p) => p.programType);
    if (roles.includes('admin')) return ranked;
    return ranked.filter((p) => roles.includes(`${p}_coordinator`));
}

// Whether this viewer, a coordinator of the request, is told editing is off
// while the faculty member revises it. The page shows the note in its
// header's body, since a long note in the actions crowds out the title.
export function editingOffForRevision(application, roles = []) {
    return (
        application?.status === 'revision_requested' &&
        coordinatedPrograms(application, roles).length > 0
    );
}

/**
 * LA's award on the request as { awarded, requested }, or null when LA has
 * not awarded support. A decision recorded with no count awards the request.
 */
export function laAwardOf(application) {
    if (laStandingOf(application) !== 'award') return null;
    const decision = application.decisions.find((d) => d.programType === 'la');
    const requested = requestedLACount(application);
    return {
        awarded: decision.awardedCount ?? decision.requestedCount ?? requested,
        requested,
    };
}

// Whether LA has decided at all: its count is then the award's, or fixed
const laDecided = (application) =>
    (application?.decisions || []).some((d) => d.programType === 'la');

const textOf = (value) => (value === undefined || value === null ? '' : value);

// The form's starting values, as the request stands
function formFrom(application) {
    const cc = application.courseConfirmation || {};
    const answers = application.programSpecificResponses || {};
    const la = answers.la || {};
    const si = answers.si || {};
    const ta = answers.ta || {};
    const award = laAwardOf(application);
    return {
        ...(award && { laAwardedCount: String(award.awarded) }),
        enrollment: textOf(cc.enrollment),
        modality: textOf(cc.modality),
        otherModality: textOf(cc.otherModality),
        partOfTermCode: textOf(cc.partOfTermCode),
        partOfTermName: textOf(cc.partOfTermName),
        meetings: meetingsFromSaved(cc),
        la: {
            requestedLACount: textOf(la.requestedLACount),
            supportUseCases: la.supportUseCases || [],
            otherSupportUseCase: textOf(la.otherSupportUseCase),
            expectedLAResponsibilities: textOf(la.expectedLAResponsibilities),
            weeklyPrepPlan: textOf(la.weeklyPrepPlan),
            weeklyMeetingHours: textOf(la.weeklyMeetingHours),
            assessmentPlan: textOf(la.assessmentPlan),
            nomineeDeclinePreference: textOf(la.nomineeDeclinePreference),
            recruitingMessage: textOf(la.recruitingMessage),
        },
        si: {
            isBottleneckCourse: !!si.isBottleneckCourse,
            expectedSessionsPerWeek: textOf(si.expectedSessionsPerWeek),
            instructorWillingToCollaborate: !!si.instructorWillingToCollaborate,
        },
        ta: {
            chairApprovalObtained: !!ta.chairApprovalObtained,
            expectedResponsibilities: textOf(ta.expectedResponsibilities),
        },
    };
}

// Fields the form holds as text that the server takes as numbers
const NUMBER_FIELDS = new Set([
    'enrollment',
    'requestedLACount',
    'weeklyMeetingHours',
    'expectedSessionsPerWeek',
]);
const asSent = (field, value) =>
    NUMBER_FIELDS.has(field) ? (value === '' ? null : Number(value)) : value;

const differs = (a, b) => JSON.stringify(a) !== JSON.stringify(b);

/**
 * The body for the fields changed since the editor opened, or null when
 * nothing changed. Only the programs the viewer edits are compared.
 */
// An "Other" description whose choice isn't "Other": the server drops it,
// so a description typed and then left behind is not a change
const orphanedOther = (field, values) =>
    (field === 'otherModality' && values.modality !== 'other') ||
    (field === 'otherSupportUseCase' &&
        !(values.supportUseCases || []).includes('other'));

export function changesBetween(initial, current, programs) {
    const body = {};
    const course = {};
    for (const field of ['enrollment', 'modality', 'otherModality']) {
        if (orphanedOther(field, current)) continue;
        if (differs(initial[field], current[field])) {
            course[field] = asSent(field, current[field]);
        }
    }
    if (
        current.partOfTermCode !== '' &&
        (differs(initial.partOfTermCode, current.partOfTermCode) ||
            differs(initial.partOfTermName, current.partOfTermName))
    ) {
        course.partOfTermCode = current.partOfTermCode;
        course.partOfTermName = current.partOfTermName;
    }
    if (differs(initial.meetings, current.meetings)) {
        course.meetings = current.meetings;
    }
    if (Object.keys(course).length > 0) body.courseConfirmation = course;

    const answers = {};
    for (const program of programs) {
        const group = {};
        for (const [field, value] of Object.entries(current[program])) {
            if (orphanedOther(field, current[program])) continue;
            if (differs(initial[program][field], value)) {
                group[field] = asSent(field, value);
            }
        }
        if (Object.keys(group).length > 0) answers[program] = group;
    }
    if (Object.keys(answers).length > 0) {
        body.programSpecificResponses = answers;
    }
    if (
        programs.includes('la') &&
        current.laAwardedCount !== undefined &&
        differs(initial.laAwardedCount, current.laAwardedCount)
    ) {
        body.laAwardedCount = Number(current.laAwardedCount);
    }
    return Object.keys(body).length > 0 ? body : null;
}

const withoutRoom = (meetings) =>
    meetings.map((meeting) => ({ ...meeting, room: undefined }));

/**
 * What in these changes can reach students already in a position: new
 * meeting times (a room alone doesn't), the modality and a lower LA count or
 * award. Empty when nothing does.
 */
const MEETINGS_CHANGED = 'changed the meeting times';
const MODALITY_CHANGED = 'changed the modality';

export function farReachingChanges(initial, current) {
    const reasons = [];
    if (differs(withoutRoom(initial.meetings), withoutRoom(current.meetings))) {
        reasons.push(MEETINGS_CHANGED);
    }
    if (
        differs(initial.modality, current.modality) ||
        (!orphanedOther('otherModality', current) &&
            differs(initial.otherModality, current.otherModality))
    ) {
        reasons.push(MODALITY_CHANGED);
    }
    const lowered = (valueOf) =>
        valueOf(current) !== '' &&
        Number(valueOf(current)) < Number(valueOf(initial));
    if (
        lowered((form) => form.la.requestedLACount) ||
        (current.laAwardedCount !== undefined &&
            lowered((form) => form.laAwardedCount))
    ) {
        reasons.push('lowered the number of LAs');
    }
    return reasons;
}

/**
 * The course fields to fill before saving, as { id, label }. Only what was
 * changed is judged, as the server does, so a request saved before a rule
 * existed can still have its other answers edited. `firstUntyped` lets the
 * first meeting of a request saved before meeting types stay untyped.
 */
export function courseFieldErrors(initial, current, firstUntyped) {
    const meetingsChanged = differs(initial.meetings, current.meetings);
    const modalityChanged =
        differs(initial.modality, current.modality) ||
        differs(initial.otherModality, current.otherModality);
    const partOfTermErrors =
        current.partOfTermCode === OTHER_PART_OF_TERM &&
        !current.partOfTermName.trim() &&
        (differs(initial.partOfTermCode, current.partOfTermCode) ||
            differs(initial.partOfTermName, current.partOfTermName))
            ? [{ id: 'ce-other-part-of-term', label: 'Part of term' }]
            : [];
    if (!meetingsChanged && !modalityChanged) return partOfTermErrors;
    return partOfTermErrors.concat(
        getCourseConfirmationOtherFieldErrors(
            {
                modality: current.modality,
                otherModality: current.otherModality,
                meetings: meetingsChanged ? current.meetings : [],
            },
            'ce',
            { required: true, firstUntyped }
        )
    );
}

function TextArea({ id, label, value, onChange }) {
    return (
        <Form.Group className="mb-3" controlId={id}>
            <Form.Label>{label}</Form.Label>
            <Form.Control
                as="textarea"
                rows={3}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        </Form.Group>
    );
}

TextArea.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

/**
 * The LA count: the request's before LA decides, the award's once LA has
 * awarded support, and fixed as filed after any other decision
 */
function LACount({
    values,
    onChange,
    award,
    decided,
    awardedCount,
    onAward,
    awardError,
}) {
    if (award) {
        return (
            <Form.Group className="mb-3" controlId="ce-la-awarded">
                <Form.Label>Number of LAs awarded</Form.Label>
                <Form.Select
                    className="w-auto"
                    value={awardedCount}
                    aria-describedby={
                        awardError
                            ? 'ce-la-awarded-error ce-la-awarded-help'
                            : 'ce-la-awarded-help'
                    }
                    isInvalid={!!awardError}
                    aria-invalid={awardError ? 'true' : undefined}
                    onChange={(e) => onAward(e.target.value)}
                >
                    {Array.from(
                        { length: award.requested },
                        (_, i) => i + 1
                    ).map((n) => (
                        <option key={n} value={String(n)}>
                            {n}
                        </option>
                    ))}
                </Form.Select>
                {awardError && (
                    <Form.Control.Feedback
                        type="invalid"
                        id="ce-la-awarded-error"
                        className="d-block"
                    >
                        {awardError}
                    </Form.Control.Feedback>
                )}
                <Form.Text id="ce-la-awarded-help">
                    Of {award.requested} requested. The faculty member&apos;s
                    request stays as they filed it; this changes LA&apos;s
                    award. It can&apos;t go below the places already accepted or
                    offered.
                </Form.Text>
            </Form.Group>
        );
    }
    if (decided) {
        return (
            <div className="mb-3">
                <p className="mb-1">Number of LAs requested</p>
                <p className="mb-0">
                    {values.requestedLACount}{' '}
                    <span className="small text-muted">
                        (LA has decided, so this stays as the faculty member
                        filed it)
                    </span>
                </p>
            </div>
        );
    }
    return (
        <Form.Group className="mb-3" controlId="ce-la-count">
            <Form.Label>Number of LAs requested</Form.Label>
            <Form.Select
                className="w-auto"
                value={values.requestedLACount}
                onChange={(e) => onChange('requestedLACount', e.target.value)}
            >
                {values.requestedLACount === '' && (
                    <option value="">Select...</option>
                )}
                {LA_COUNTS.map((n) => (
                    <option key={n} value={String(n)}>
                        {n}
                    </option>
                ))}
            </Form.Select>
        </Form.Group>
    );
}

LACount.propTypes = {
    values: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    award: PropTypes.shape({ requested: PropTypes.number.isRequired }),
    decided: PropTypes.bool.isRequired,
    awardedCount: PropTypes.string,
    onAward: PropTypes.func.isRequired,
    awardError: PropTypes.string,
};

function LAFields({ values, onChange, hasNominees, count }) {
    const toggleUseCase = (value) => {
        const current = values.supportUseCases;
        onChange(
            'supportUseCases',
            current.includes(value)
                ? current.filter((v) => v !== value)
                : [...current, value]
        );
    };
    return (
        <fieldset className="border rounded p-3 mb-3">
            <legend className="fs-6 fw-semibold mb-2">LA answers</legend>
            <LACount values={values} onChange={onChange} {...count} />
            <fieldset className="mb-3">
                <legend className="fs-6 mb-1">
                    How LAs will support the course
                </legend>
                {LA_SUPPORT_USE_CASE_OPTIONS.map((option) => (
                    <Form.Check
                        key={option.value}
                        id={`ce-la-use-${option.value}`}
                        type="checkbox"
                        label={option.label}
                        checked={values.supportUseCases.includes(option.value)}
                        onChange={() => toggleUseCase(option.value)}
                    />
                ))}
                {values.supportUseCases.includes('other') && (
                    <Form.Group
                        className="mt-2"
                        controlId="ce-la-use-other-text"
                    >
                        <Form.Label>Describe the other use</Form.Label>
                        <Form.Control
                            type="text"
                            value={values.otherSupportUseCase}
                            onChange={(e) =>
                                onChange('otherSupportUseCase', e.target.value)
                            }
                        />
                    </Form.Group>
                )}
            </fieldset>
            <TextArea
                id="ce-la-responsibilities"
                label="LA responsibilities"
                value={values.expectedLAResponsibilities}
                onChange={(v) => onChange('expectedLAResponsibilities', v)}
            />
            <TextArea
                id="ce-la-prep-plan"
                label="Weekly preparation plan"
                value={values.weeklyPrepPlan}
                onChange={(v) => onChange('weeklyPrepPlan', v)}
            />
            <Form.Group className="mb-3" controlId="ce-la-meeting-hours">
                <Form.Label>Weekly LA meeting length</Form.Label>
                <Form.Select
                    className="w-auto"
                    value={String(values.weeklyMeetingHours)}
                    onChange={(e) =>
                        onChange('weeklyMeetingHours', e.target.value)
                    }
                >
                    {values.weeklyMeetingHours === '' && (
                        <option value="">Select...</option>
                    )}
                    {MEETING_HOUR_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={String(value)}>
                            {label}
                        </option>
                    ))}
                </Form.Select>
            </Form.Group>
            <TextArea
                id="ce-la-assessment"
                label="Assessment plan"
                value={values.assessmentPlan}
                onChange={(v) => onChange('assessmentPlan', v)}
            />
            {hasNominees && (
                <Form.Group className="mb-3" controlId="ce-la-decline">
                    <Form.Label>If a nominee declines</Form.Label>
                    <Form.Select
                        value={values.nomineeDeclinePreference}
                        onChange={(e) =>
                            onChange('nomineeDeclinePreference', e.target.value)
                        }
                    >
                        {values.nomineeDeclinePreference === '' && (
                            <option value="">Select...</option>
                        )}
                        {LA_NOMINEE_DECLINE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
            )}
            <TextArea
                id="ce-la-recruiting"
                label="Recruiting message"
                value={values.recruitingMessage}
                onChange={(v) => onChange('recruitingMessage', v)}
            />
            <p className="small text-muted mb-0">
                LA nominees are changed in the LA nominees section of the
                request.
            </p>
        </fieldset>
    );
}

LAFields.propTypes = {
    values: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    hasNominees: PropTypes.bool.isRequired,
    count: PropTypes.object.isRequired,
};

function SIFields({ values, onChange }) {
    return (
        <fieldset className="border rounded p-3 mb-3">
            <legend className="fs-6 fw-semibold mb-2">SI answers</legend>
            <Form.Check
                id="ce-si-bottleneck"
                className="mb-2"
                type="checkbox"
                label="Bottleneck course"
                checked={values.isBottleneckCourse}
                onChange={(e) =>
                    onChange('isBottleneckCourse', e.target.checked)
                }
            />
            <Form.Group className="mb-2" controlId="ce-si-sessions">
                <Form.Label>SI sessions per week</Form.Label>
                <Form.Control
                    className="w-auto"
                    type="number"
                    min={0}
                    max={20}
                    value={values.expectedSessionsPerWeek}
                    onChange={(e) =>
                        onChange('expectedSessionsPerWeek', e.target.value)
                    }
                />
            </Form.Group>
            <Form.Check
                id="ce-si-collaborate"
                type="checkbox"
                label="The instructor is willing to collaborate with the SI leader"
                checked={values.instructorWillingToCollaborate}
                onChange={(e) =>
                    onChange('instructorWillingToCollaborate', e.target.checked)
                }
            />
        </fieldset>
    );
}

SIFields.propTypes = {
    values: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
};

function TAFields({ values, onChange }) {
    return (
        <fieldset className="border rounded p-3 mb-3">
            <legend className="fs-6 fw-semibold mb-2">TA answers</legend>
            <Form.Check
                id="ce-ta-chair"
                className="mb-2"
                type="checkbox"
                label="Chair approval obtained"
                checked={values.chairApprovalObtained}
                onChange={(e) =>
                    onChange('chairApprovalObtained', e.target.checked)
                }
            />
            <TextArea
                id="ce-ta-responsibilities"
                label="TA responsibilities"
                value={values.expectedResponsibilities}
                onChange={(v) => onChange('expectedResponsibilities', v)}
            />
        </fieldset>
    );
}

TAFields.propTypes = {
    values: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
};

const PROGRAM_FIELDS = { la: LAFields, si: SIFields, ta: TAFields };

const PROGRAM_NAMES = { la: 'LA', si: 'SI', ta: 'TA' };

const reachesOwn = (impact) =>
    impact.programs.some(
        (p) => p.accepted.length + p.offers.length + p.applicants.length > 0
    );

// New meeting times or a new modality reach another program's students too;
// a lower count reaches only LA's
const reachesOther = (impact, reasons) =>
    !!impact.otherProgramsPlaced &&
    (reasons.includes(MEETINGS_CHANGED) || reasons.includes(MODALITY_CHANGED));

const reachesAnyone = (impact, reasons) =>
    reachesOwn(impact) || reachesOther(impact, reasons);

function NameList({ label, names }) {
    if (names.length === 0) return null;
    return (
        <>
            <p className="mb-1 fw-semibold">
                {label} ({names.length})
            </p>
            <ul className="mb-2">
                {names.map((name, i) => (
                    <li key={i}>{name}</li>
                ))}
            </ul>
        </>
    );
}

NameList.propTypes = {
    label: PropTypes.string.isRequired,
    names: PropTypes.arrayOf(PropTypes.string).isRequired,
};

/**
 * Who a far-reaching change reaches, shown before it saves. `impact` is null
 * when the check failed, so the coordinator still decides knowingly.
 */
// "A", "A and B", "A, B and C"
const joinReasons = (reasons) =>
    reasons.length < 3
        ? reasons.join(' and ')
        : `${reasons.slice(0, -1).join(', ')} and ${reasons[reasons.length - 1]}`;

const ImpactWarning = React.forwardRef(function ImpactWarning(
    { reasons, impact },
    ref
) {
    return (
        <Alert ref={ref} tabIndex={-1} variant="warning" className="mb-0">
            <h3 className="h6">Check who this reaches before you save</h3>
            <p>
                You {joinReasons(reasons)}.{' '}
                {!impact
                    ? "We couldn't check which students this reaches. Go back and try again, or save anyway."
                    : reachesOwn(impact)
                      ? 'These students are already in this position:'
                      : ''}
            </p>
            {impact?.programs.map((p) =>
                p.accepted.length + p.offers.length + p.applicants.length >
                0 ? (
                    <div key={p.program}>
                        {impact.programs.length > 1 && (
                            <h4 className="h6 mt-2">
                                {PROGRAM_NAMES[p.program]} position
                            </h4>
                        )}
                        <NameList label="Accepted" names={p.accepted} />
                        <NameList
                            label="Offers awaiting an answer"
                            names={p.offers}
                        />
                        <NameList
                            label="Applicants under review"
                            names={p.applicants}
                        />
                    </div>
                ) : null
            )}
            {impact && reachesOther(impact, reasons) && (
                <p className={reachesOwn(impact) ? undefined : 'mb-0'}>
                    This also changes the section for students placed by another
                    program. Let that program know.
                </p>
            )}
            {(!impact || reachesOwn(impact)) && (
                <p className="mb-0 small">
                    Saving emails only the faculty member, so tell these
                    students about the change yourself.
                </p>
            )}
        </Alert>
    );
});

ImpactWarning.propTypes = {
    reasons: PropTypes.arrayOf(PropTypes.string).isRequired,
    impact: PropTypes.shape({
        programs: PropTypes.array.isRequired,
        otherProgramsPlaced: PropTypes.bool,
    }),
};

/**
 * "Edit request" and its editor, for a coordinator of a program the request
 * ranks, or an admin. Renders nothing for anyone else, or while the
 * faculty member is revising the request (editingOffForRevision). onSaved gets the
 * server's answer ({ application, changed, emailed }).
 */
// The section's part of term: the term's sessions, then Other with its own
// text. A saved answer the list no longer holds stays chosen.
function PartOfTermEditor({ form, partsOfTerm, setCourse, hasError }) {
    const code = form.partOfTermCode;
    const parts = partsOfTerm || [];
    const kept =
        code &&
        code !== OTHER_PART_OF_TERM &&
        !parts.some((p) => p.code === code)
            ? [{ code, name: form.partOfTermName || code }]
            : [];
    const all = [...parts, ...kept];
    const choose = (chosen) => {
        setCourse('partOfTermCode', chosen);
        setCourse(
            'partOfTermName',
            chosen === OTHER_PART_OF_TERM
                ? ''
                : all.find((p) => p.code === chosen)?.name || ''
        );
    };
    return (
        <>
            <Form.Group className="mb-3" controlId="ce-part-of-term">
                <Form.Label>Part of term</Form.Label>
                <Form.Select
                    className="mw-100"
                    value={code}
                    onChange={(e) => choose(e.target.value)}
                    aria-describedby="ce-part-of-term-help"
                >
                    {code === '' && <option value="">Not asked</option>}
                    {all.map((part) => (
                        <option key={part.code} value={part.code}>
                            {partOfTermOptionLabel(part)}
                        </option>
                    ))}
                    <option value={OTHER_PART_OF_TERM}>Other</option>
                </Form.Select>
                {code === '' && (
                    <Form.Text id="ce-part-of-term-help">
                        This request was filed before the form asked. Choose the
                        section&apos;s part of term.
                    </Form.Text>
                )}
            </Form.Group>
            {code === OTHER_PART_OF_TERM && (
                <Form.Group className="mb-3" controlId="ce-other-part-of-term">
                    <Form.Label>Describe the part of term</Form.Label>
                    <Form.Control
                        type="text"
                        maxLength={PART_OF_TERM_MAX_LENGTH}
                        value={form.partOfTermName}
                        isInvalid={hasError('ce-other-part-of-term')}
                        aria-invalid={hasError('ce-other-part-of-term')}
                        onChange={(e) =>
                            setCourse('partOfTermName', e.target.value)
                        }
                    />
                    <Form.Control.Feedback type="invalid">
                        This field is required.
                    </Form.Control.Feedback>
                </Form.Group>
            )}
        </>
    );
}

function CoordinatorEditRequest({ application, roles, onSaved }) {
    const programs = coordinatorEditablePrograms(application, roles);
    const [show, setShow] = useState(false);
    const [initial, setInitial] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // The server's refusal of the award count, shown beside that field
    const [awardError, setAwardError] = useState(null);
    // Field errors show once a save was tried
    const [attempted, setAttempted] = useState(false);
    // { reasons, impact } while the editor shows who a change reaches
    const [warning, setWarning] = useState(null);
    // The term's sessions, read when the editor opens
    const [partsOfTerm, setPartsOfTerm] = useState(null);
    const saveRef = useRef(null);
    const errorRef = useRevealAlert(error);
    const warningRef = useRevealAlert(warning);
    // The answer of a save, handed over once the dialog has closed: the
    // page then moves focus to its note, which the dialog would otherwise
    // take back to the button that opened it
    const [savedAnswer, setSavedAnswer] = useState(null);

    // The refused field takes the focus, as a form's first invalid field does
    useEffect(() => {
        if (awardError) document.getElementById('ce-la-awarded')?.focus();
    }, [awardError]);

    if (programs.length === 0) return null;

    const courseLabel =
        `${application.course?.subject ?? ''} ${application.course?.courseNumber ?? ''}`.trim();
    const section = application.courseConfirmation?.sectionNumber;
    const heading =
        courseLabel && section
            ? `${courseLabel}, section ${section}`
            : courseLabel;
    const titleId = `coordinator-edit-title-${application._id}`;
    const award = laAwardOf(application);
    // The first meeting of a request saved before meeting types may stay
    // untyped, as on the faculty member's own form
    const firstUntyped = !application.courseConfirmation?.meetings?.[0]?.type;
    const fieldErrors = form
        ? courseFieldErrors(initial, form, firstUntyped)
        : [];
    const hasError = (id) => attempted && fieldErrors.some((e) => e.id === id);

    const open = () => {
        const start = formFrom(application);
        setInitial(start);
        setForm(start);
        setError(null);
        setAwardError(null);
        setAttempted(false);
        setWarning(null);
        setSavedAnswer(null);
        setShow(true);
        termService
            .getTermByCode(application.term)
            .then((term) => setPartsOfTerm(partsOfTermOf(term)))
            .catch(() => setPartsOfTerm(partsOfTermOf(null)));
    };

    const handOver = () => {
        if (!savedAnswer) return;
        onSaved(savedAnswer);
        setSavedAnswer(null);
    };

    const close = () => {
        if (saving) return;
        setShow(false);
    };

    const setCourse = (field, value) =>
        setForm((prev) => ({ ...prev, [field]: value }));
    const setAnswer = (program) => (field, value) =>
        setForm((prev) => ({
            ...prev,
            [program]: { ...prev[program], [field]: value },
        }));

    const submit = async (changes) => {
        setSaving(true);
        try {
            const result = await applicationService.coordinatorEdit(
                application._id,
                changes
            );
            setSavedAnswer(result);
            setShow(false);
        } catch (err) {
            setWarning(null);
            const refusal = err.response?.data;
            if (refusal?.field === 'laAwardedCount' && refusal.message) {
                setAwardError(refusal.message);
            } else {
                setError(
                    refusal?.message ||
                        'The changes were not saved. Please try again.'
                );
            }
        } finally {
            setSaving(false);
        }
    };

    const save = async (event) => {
        event.preventDefault();
        setError(null);
        setAwardError(null);
        setAttempted(true);
        if (fieldErrors.length > 0) {
            setError(`Missing: ${fieldErrors.map((e) => e.label).join(', ')}.`);
            return;
        }
        const changes = changesBetween(initial, form, programs);
        if (!changes) {
            setSavedAnswer({ application, changed: [], emailed: false });
            setShow(false);
            return;
        }
        // Shown once: "Save anyway" comes back here with the warning up
        const reasons = farReachingChanges(initial, form);
        if (!warning && reasons.length > 0) {
            setSaving(true);
            let impact = null;
            try {
                impact = await applicationService.coordinatorEditImpact(
                    application._id
                );
            } catch {
                impact = null;
            }
            setSaving(false);
            if (!impact || reachesAnyone(impact, reasons)) {
                setWarning({ reasons, impact });
                return;
            }
        }
        await submit(changes);
    };

    return (
        <>
            <Button
                variant="outline-primary"
                size="sm"
                onClick={open}
                aria-label={`Edit request for ${heading || 'this course'}`}
            >
                Edit request
            </Button>
            <Modal
                show={show}
                onHide={close}
                onExited={handOver}
                restoreFocus={!savedAnswer}
                size="lg"
                fullscreen="sm-down"
                scrollable
                aria-labelledby={titleId}
            >
                {form && (
                    // A flex column, so the body scrolls between the header
                    // and the footer instead of running past them, and the
                    // course and section in the title stay in view
                    <Form
                        noValidate
                        onSubmit={save}
                        className="d-flex flex-column flex-grow-1 overflow-hidden"
                    >
                        <Modal.Header closeButton>
                            <Modal.Title id={titleId} as="h2" className="h5">
                                Edit request
                                {heading ? `: ${heading}` : ''}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {warning ? (
                                <ImpactWarning
                                    ref={warningRef}
                                    reasons={warning.reasons}
                                    impact={warning.impact}
                                />
                            ) : (
                                <>
                                    <p>
                                        Your changes apply to the request as it
                                        stands. Its status stays the same, and
                                        the faculty member is emailed a summary
                                        of what changed. The program ranking,
                                        the CRN and the section stay as the
                                        faculty member filed them.
                                    </p>
                                    <fieldset className="border rounded p-3 mb-3">
                                        <legend className="fs-6 fw-semibold mb-2">
                                            Course details
                                        </legend>
                                        <Form.Group
                                            className="mb-3"
                                            controlId="ce-enrollment"
                                        >
                                            <Form.Label>
                                                Maximum course enrollment
                                            </Form.Label>
                                            <Form.Control
                                                className="w-auto"
                                                type="number"
                                                min={1}
                                                value={form.enrollment}
                                                onChange={(e) =>
                                                    setCourse(
                                                        'enrollment',
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </Form.Group>
                                        <PartOfTermEditor
                                            form={form}
                                            partsOfTerm={partsOfTerm}
                                            setCourse={setCourse}
                                            hasError={hasError}
                                        />
                                        <Form.Group
                                            className="mb-3"
                                            controlId="ce-modality"
                                        >
                                            <Form.Label>Modality</Form.Label>
                                            <Form.Select
                                                className="w-auto"
                                                value={form.modality}
                                                onChange={(e) =>
                                                    setCourse(
                                                        'modality',
                                                        e.target.value
                                                    )
                                                }
                                            >
                                                {form.modality === '' && (
                                                    <option value="">
                                                        Select...
                                                    </option>
                                                )}
                                                {MODALITY_OPTIONS.map(
                                                    (option) => (
                                                        <option
                                                            key={option.value}
                                                            value={option.value}
                                                        >
                                                            {option.label}
                                                        </option>
                                                    )
                                                )}
                                            </Form.Select>
                                        </Form.Group>
                                        {form.modality === 'other' && (
                                            <Form.Group
                                                className="mb-3"
                                                controlId="ce-other-modality"
                                            >
                                                <Form.Label>
                                                    Describe the modality
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={form.otherModality}
                                                    isInvalid={hasError(
                                                        'ce-other-modality'
                                                    )}
                                                    onChange={(e) =>
                                                        setCourse(
                                                            'otherModality',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </Form.Group>
                                        )}
                                        <MeetingFields
                                            meetings={form.meetings}
                                            onChange={(meetings) =>
                                                setCourse('meetings', meetings)
                                            }
                                            hasError={hasError}
                                            idPrefix="ce"
                                            inDialog
                                        />
                                    </fieldset>
                                    {programs.map((program) => {
                                        const Fields = PROGRAM_FIELDS[program];
                                        return (
                                            <Fields
                                                key={program}
                                                values={form[program]}
                                                onChange={setAnswer(program)}
                                                hasNominees={
                                                    nomineesOf(application)
                                                        .length > 0
                                                }
                                                count={{
                                                    award,
                                                    decided:
                                                        laDecided(application),
                                                    awardedCount:
                                                        form.laAwardedCount,
                                                    awardError,
                                                    onAward: (value) => {
                                                        setAwardError(null);
                                                        setCourse(
                                                            'laAwardedCount',
                                                            value
                                                        );
                                                    },
                                                }}
                                            />
                                        );
                                    })}
                                </>
                            )}
                            {error && (
                                <Alert
                                    ref={errorRef}
                                    tabIndex={-1}
                                    variant="danger"
                                    className="mb-0 mt-3"
                                >
                                    {error}
                                </Alert>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            {warning ? (
                                <Button
                                    variant="outline-primary"
                                    onClick={() => {
                                        setWarning(null);
                                        // The button it replaces is gone
                                        saveRef.current?.focus();
                                    }}
                                    disabled={saving}
                                >
                                    Go back
                                </Button>
                            ) : (
                                <Button
                                    variant="outline-primary"
                                    onClick={close}
                                    disabled={saving}
                                >
                                    Cancel
                                </Button>
                            )}
                            <Button
                                ref={saveRef}
                                type="submit"
                                variant="primary"
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-2"
                                            aria-hidden="true"
                                        />
                                        Saving…
                                    </>
                                ) : warning ? (
                                    'Save anyway'
                                ) : (
                                    'Save changes'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Modal>
        </>
    );
}

CoordinatorEditRequest.propTypes = {
    application: PropTypes.shape({
        _id: PropTypes.string,
        status: PropTypes.string,
        course: PropTypes.object,
        programPreferences: PropTypes.array,
        courseConfirmation: PropTypes.object,
        decisions: PropTypes.array,
        programSpecificResponses: PropTypes.object,
        namedStudents: PropTypes.array,
    }).isRequired,
    roles: PropTypes.arrayOf(PropTypes.string).isRequired,
    onSaved: PropTypes.func.isRequired,
};

export default CoordinatorEditRequest;
